import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { SITE, IMG } from "@/config/site";

const Block = ({ title, children }) => (
  <div className="border-t border-[#17251C]/14 py-8">
    <h2 className="display-xl text-xl text-[#17251C] md:text-2xl">{title}</h2>
    <div className="mt-4 space-y-3 text-base leading-relaxed text-[#6E7A69]">{children}</div>
  </div>
);

export default function Datenschutz() {
  return (
    <>
      <Seo
        title="Datenschutzerklärung | Hanse Garten Bremen"
        description="Datenschutzerklärung von Hanse Garten Bremen: Informationen zur Verarbeitung Ihrer Daten bei Kontaktformular, Anfrageformular, WhatsApp-Kontakt und Hosting."
        path="/datenschutz"
        image={IMG.logo}
      />
      <PageHero
        eyebrow="Rechtliches"
        titleLines={["Datenschutz"]}
        text="Informationen zur Verarbeitung personenbezogener Daten auf dieser Website gemäß DSGVO."
        breadcrumb="Datenschutz"
      />

      <section className="bg-[#F3EDE2] py-20 md:py-28">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <Block title="1. Verantwortlicher">
            <p>
              {SITE.name}
              <br />
              Inhaber: {SITE.owner} ({SITE.legalForm})
              <br />
              {SITE.street}, {SITE.zipCity}, {SITE.country}
              <br />
              Kontakt: E-Mail {SITE.email} · WhatsApp {SITE.whatsappNumber}
            </p>
          </Block>

          <Block title="2. Hosting und Server-Logfiles">
            <p>
              Diese Website wird bei einem Dienstleister (Hoster) betrieben. Beim Aufruf der Seite erhebt der Server
              automatisch Informationen in sogenannten Server-Logfiles: IP-Adresse, Datum und Uhrzeit des Zugriffs,
              aufgerufene Seite, übertragene Datenmenge, Referrer-URL sowie Browser- und Betriebssystemangaben.
            </p>
            <p>
              Diese Daten sind technisch erforderlich, um die Website bereitzustellen und ihre Stabilität und
              Sicherheit zu gewährleisten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an
              einem sicheren, funktionsfähigen Webangebot).
            </p>
          </Block>

          <Block title="3. Kontakt- und Angebotsformular">
            <p>
              Wenn Sie uns über das Anfrageformular kontaktieren, verarbeiten wir die von Ihnen eingegebenen Daten
              (Vorname, Nachname, E-Mail-Adresse, optional Telefonnummer, Ort, Art des Projekts, gewünschte Leistung,
              Wunschzeitraum und Ihre Nachricht), um Ihre Anfrage zu bearbeiten und Ihnen zu antworten.
            </p>
            <p>
              Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Vertrag bzw. Vertragsanbahnung) sowie Art. 6 Abs. 1 lit. a
              DSGVO, soweit Sie freiwillig weitere Angaben machen. Die Daten werden auf unserem Server gespeichert und
              gelöscht, sobald sie für die Bearbeitung nicht mehr erforderlich sind und keine gesetzlichen
              Aufbewahrungspflichten entgegenstehen.
            </p>
          </Block>

          <Block title="4. E-Mail-Kontakt">
            <p>
              Wenn Sie uns per E-Mail an {SITE.email} schreiben, verarbeiten wir Ihre E-Mail-Adresse, Ihren Namen und
              den Inhalt Ihrer Nachricht (inklusive freiwillig angehängter Fotos) ausschließlich zur Bearbeitung Ihres
              Anliegens. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b und f DSGVO. Die Daten werden gelöscht, sobald sie
              für die Bearbeitung nicht mehr erforderlich sind.
            </p>
          </Block>

          <Block title="5. WhatsApp-Kontakt">
            <p>
              Auf dieser Website befinden sich Verlinkungen zu WhatsApp (Anbieter: WhatsApp Ireland Limited). Es werden
              keine WhatsApp-Inhalte oder -Skripte in die Website eingebettet – erst wenn Sie einen WhatsApp-Button
              anklicken, öffnet sich WhatsApp und Ihre Daten werden an den Anbieter übermittelt.
            </p>
            <p>
              Für die Verarbeitung Ihrer Daten innerhalb von WhatsApp gelten die Datenschutzbestimmungen von WhatsApp.
              Wenn Sie uns über WhatsApp schreiben, verarbeiten wir Ihre Telefonnummer, Ihren Namen und Ihre Nachrichten
              (inklusive freiwillig übermittelter Fotos) zur Bearbeitung Ihres Anliegens. Rechtsgrundlage ist Art. 6
              Abs. 1 lit. b und f DSGVO.
            </p>
          </Block>

          <Block title="6. Bild-Uploads bzw. übermittelte Fotos">
            <p>
              Sie können uns freiwillig Fotos Ihres Gartens per WhatsApp senden. Diese Bilder verwenden wir
              ausschließlich zur Einschätzung und Planung Ihres Projekts. Eine Veröffentlichung erfolgt nur mit Ihrer
              ausdrücklichen Zustimmung.
            </p>
          </Block>

          <Block title="7. Cookies">
            <p>
              Diese Website verwendet ausschließlich technisch notwendige Speichermechanismen. Dazu gehört ein lokaler
              Speichereintrag (localStorage), der Ihre Entscheidung zum Cookie-Hinweis festhält, damit dieser nicht bei
              jedem Besuch erneut erscheint. Rechtsgrundlage ist § 25 Abs. 2 TDDDG bzw. Art. 6 Abs. 1 lit. f DSGVO.
            </p>
          </Block>

          <Block title="8. Analyse, Tracking, Google Maps und externe Schriftarten">
            <p>
              Wir setzen keine Analyse- oder Trackingdienste ein. Es findet keine Reichweitenmessung und kein
              Profiling statt.
            </p>
            <p>
              Eine Einbindung von Google Maps erfolgt nicht; das Einsatzgebiet wird über eine eigene, lokal erzeugte
              Grafik dargestellt.
            </p>
            <p>
              Für die Typografie werden Schriftarten über Google Fonts geladen. Dabei stellt Ihr Browser eine
              Verbindung zu Servern von Google her, wobei Ihre IP-Adresse übertragen wird. Rechtsgrundlage ist Art. 6
              Abs. 1 lit. f DSGVO (einheitliche, barrierefreie Darstellung der Website).
            </p>
          </Block>

          <Block title="9. Ihre Rechte">
            <p>
              Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16), Löschung (Art. 17),
              Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch gegen die
              Verarbeitung (Art. 21 DSGVO). Eine erteilte Einwilligung können Sie jederzeit widerrufen.
            </p>
            <p>
              Zudem haben Sie das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren, etwa bei der
              Landesbeauftragten für Datenschutz und Informationsfreiheit der Freien Hansestadt Bremen.
            </p>
          </Block>

          <Block title="10. Aktualität">
            <p>
              Diese Datenschutzerklärung wird angepasst, wenn sich die technischen Funktionen dieser Website oder die
              rechtlichen Vorgaben ändern. Stand: 2026.
            </p>
          </Block>
        </div>
      </section>
    </>
  );
}
